import { Fragment } from 'react';

import { OrganizationLogo } from 'src/components/Organization';
import { AppAvatar } from 'src/widgets/AppCard';
import { productTypeMap } from 'src/shared/mappings/productTypeMap';
import { Skeleton } from '@/components/ui/skeleton';

import type { IInvitationDetails } from '../model';

const DetailsRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="grid grid-cols-1 items-center gap-x-4 gap-y-1 px-3 py-2.5 text-sm sm:grid-cols-[200px_16px_1fr]">
    <dt className="font-semibold text-slate-700">{label}</dt>
    <span aria-hidden className="hidden text-muted-foreground sm:inline">
      :
    </span>
    <dd className="flex min-w-0 items-center gap-2 text-foreground">{value}</dd>
  </div>
);

type Props = {
  details: IInvitationDetails;
};

export function InvitationDetails({ details }: Props) {
  const { appName, productType, role, requester } = details;
  const { icon: Icon, label: escrowLabel } = productTypeMap[productType];

  return (
    <dl className="divide-y divide-border rounded-lg border border-border">
      <DetailsRow
        label="Application"
        value={
          <Fragment>
            <AppAvatar appId={appName} name={appName} claimed shape="circle" size="xs" />
            <span className="truncate">{appName}</span>
          </Fragment>
        }
      />
      <DetailsRow
        label="Escrow"
        value={
          <Fragment>
            <Icon className="size-5 shrink-0" />
            <span className="truncate">{escrowLabel}</span>
          </Fragment>
        }
      />
      <DetailsRow label="Your role" value={role} />
      <DetailsRow
        label="Requested by"
        value={
          requester ? (
            <Fragment>
              <OrganizationLogo logo={requester.logo} name={requester.name} size="sm" className="shrink-0" />
              <span className="truncate font-semibold">{requester.name}</span>
            </Fragment>
          ) : (
            '—'
          )
        }
      />
    </dl>
  );
}

export const InvitationDetailsSkeleton = () => (
  <div className="divide-y divide-border rounded-lg border border-border" data-testid="invitation-details-skeleton">
    {Array.from({ length: 4 }, (_, index) => (
      <div
        key={index}
        className="grid grid-cols-1 items-center gap-x-4 gap-y-1 px-4 py-3 text-sm sm:grid-cols-[200px_16px_1fr]"
      >
        <Skeleton className="h-4 w-20 rounded" />
        <span className="hidden sm:inline" />
        <Skeleton className="h-4 w-32 rounded" />
      </div>
    ))}
  </div>
);
